import type { FC } from "react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import "rc-slider/assets/index.css";
import Slider from "rc-slider";
import { colors } from "@/styles/utils/vars";
import { CURRENCY } from "@/utils/constants";
import { convertPennyToRub, convertRubToPenny } from "@/utils/common/helpers";
import { cssRange, StyledRangeContainer, StyledRangeInput, StyledRangeWrapper } from "./StyledRange";
import { Field } from "../Field/Field";
import { Typography } from "../Typography/Typography";
export type RangePropsType = {
  onAfterChange?: (value?: number[]) => void;
  defaultValue: [number, number];
  value?: [number, number];
  isFormatPrice?: boolean;
};
const valueToNumber = (value: number | number[]): number[] => [+(value[0] || 0), +(value[1] || 0)];
const priceFromFormat = (value: string, isFormatPrice: boolean) => isFormatPrice ? convertRubToPenny(+(value || 0)) : +(value || 0);
export const Range: FC<RangePropsType> = memo(({
  isFormatPrice = true,
  defaultValue: [defaultMin, defaultMax],
  value: valueOut,
  onAfterChange
}) => {
  const [valueMin, valueMax] = useMemo(() => !!valueOut ? valueOut : [undefined, undefined], [valueOut]);
  const [[currentMin, currentMax], setCurrentValue] = useState<number[]>([valueMin || 0, valueMax || 0]);
  const onAfterChangeHandle = useCallback((value: number[]) => {
    const min = value[0];
    const max = value[1];
    if (min === defaultMin && max === defaultMax || min === valueMin && max === valueMax) {
      return;
    }
    if (onAfterChange !== undefined) {
      onAfterChange([min, max]);
    }
  }, [defaultMax, defaultMin, onAfterChange, valueMax, valueMin]);
  const onChangeFieldMin = useCallback((value: string) => {
    const num = priceFromFormat(value, isFormatPrice);
    setCurrentValue([num > 0 ? num : currentMin, currentMax]);
  }, [currentMax, currentMin, isFormatPrice]);
  const onChangeFieldMax = useCallback((value: string) => {
    const num = priceFromFormat(value, isFormatPrice);
    setCurrentValue([currentMin, num > 0 ? num : currentMax]);
  }, [currentMax, currentMin, isFormatPrice]);
  useEffect(() => {
    setCurrentValue([valueMin ?? defaultMin, valueMax ?? defaultMax]);
  }, [defaultMax, defaultMin, valueMax, valueMin]);
  return <StyledRangeContainer>
        <StyledRangeWrapper>
          <Typography variant={"p10"}>
            {isFormatPrice ? convertPennyToRub(defaultMin) : defaultMin}{" "}
            {CURRENCY}
          </Typography>
          <Slider range min={defaultMin} max={defaultMax} allowCross={false} defaultValue={[defaultMin, defaultMax]} value={[currentMin, currentMax]} onChange={val => {
        setCurrentValue(valueToNumber(val));
      }} onAfterChange={val => onAfterChangeHandle(valueToNumber(val))} className={cssRange} />
          <Typography variant={"p10"}>
            {isFormatPrice ? convertPennyToRub(defaultMax) : defaultMax}{" "}
            {CURRENCY}
          </Typography>
        </StyledRangeWrapper>

        <StyledRangeInput>
          <Typography variant={"p12"} color={colors.grayDark}>
            от
          </Typography>
          <Field name={"priceMin"} value={isFormatPrice ? convertPennyToRub(currentMin) : currentMin} withAnimatingLabel={false} onChange={({
        target: {
          value
        }
      }) => onChangeFieldMin(value)} onBlur={({
        target: {
          value
        }
      }) => {
        const val = [priceFromFormat(value, isFormatPrice), currentMax];
        onAfterChangeHandle(val);
      }} />
        </StyledRangeInput>
        <StyledRangeInput>
          <Typography variant={"p12"} color={colors.grayDark}>
            до
          </Typography>
          <Field name={"priceMax"} value={isFormatPrice ? convertPennyToRub(currentMax) : currentMax} withAnimatingLabel={false} onChange={({
        target: {
          value
        }
      }) => onChangeFieldMax(value)} onBlur={({
        target: {
          value
        }
      }) => {
        const val = [currentMin, priceFromFormat(value, isFormatPrice)];
        onAfterChangeHandle(val);
      }} />
        </StyledRangeInput>
      </StyledRangeContainer>;
});
Range.displayName = "Range";